/**
 * « Personne sur place » (« Coordonnées de la personne présente » au prototype, renommée avec
 * #555) — la troisième carte de l'onglet « Infos »
 * (`mocks/ghp-ct/screens.js:367-376`), pleine largeur.
 *
 * ⚠️ **Elle vit ici depuis le 21/09/2026, et aussi en tête de « Transmettre » depuis le
 * 20/10/2026 (#554).** Un test terrain l'a réclamée dans le premier onglet : l'agent arrive devant
 * la maison, personne n'ouvre, il lui faut **un numéro tout de suite**. La recette de GHP la veut
 * aussi au-dessus de la signature (frames 1.10c / 1.10d) : qui signe voit sous quel nom.
 *
 * ⚠️ **Une seule saisie, pas deux.** Ce sont les mêmes champs que la fiche transmet
 * (`metPerson`) : la personne présente **est** celle qui signera, ce que le prototype dit dans
 * sa pastille. Une seconde copie en lecture seule de ce que le bureau a noté a été écrite puis
 * retirée — deux blocs pour un même état, et l'agent n'aurait pas su lequel fait foi.
 *
 * ⚠️ **Une carte seulement sans contact sur la demande** (#555, recette du 20/10/2026 : « Fusionner
 * les deux cases ») : c'est alors la seule saisie du signataire. Avec un contact, ses champs
 * (`AttendeeFields`) sont un volet de la carte « Contact », que la case « Une autre personne est
 * sur place » ouvre, vide — rien n'y est recopié de la demande, l'agent saisit qui il a devant lui
 * (M21, D4). Par défaut, le contact est celui qui signe (`metPersonOf`).
 */
import { CAPACITY_SUGGESTIONS, phoneWarning } from '@ghp/domain'
import { Card, CardBody, CardHeader, Field, InfoNote, Input } from '@ghp/ui'
import { Info, UserRound } from 'lucide-react'

import type { InspectionDraft } from '../../../offline/data-db'
import s from './AttendeeCard.module.css'
import claim from './claimed.module.css'

export interface AttendeeCardProps {
  draft: InspectionDraft
  /** Verrouillée quand la fiche est partie : on lit, on ne réécrit plus. */
  readOnly?: boolean
  onDraftChange?: (patch: Partial<InspectionDraft>) => void
}

function isBlank(value: string | null | undefined): boolean {
  return value === null || value === undefined || value.trim() === ''
}

export function AttendeeCard(props: AttendeeCardProps) {
  return (
    <Card className={s.card}>
      <CardHeader icon={<UserRound size={19} />} title="Personne sur place">
        {/* La pastille du prototype, verbatim : elle dit en trois mots pourquoi ces champs sont
            saisissables ici alors que le reste de l'onglet est en lecture.
            ⚠️ Un `<span>` et non `Badge` : le prototype l'écrit `badge neutral plain`, et le
            composant n'a ni le ton neutre ni la variante sans pastille. Le module CSS reprend sa
            géométrie telle quelle — inventer un cinquième ton de `Badge` serait une décision de
            design system, qui ne se prend pas ici. */}
        <span className={s.signatory}>Signataire · modifiable sur le terrain</span>
      </CardHeader>
      <CardBody>
        <AttendeeFields {...props} />
      </CardBody>
    </Card>
  )
}

/** Les champs du signataire : la carte ci-dessus, ou le volet de la carte « Contact » (#555). */
export function AttendeeFields({ draft, readOnly, onDraftChange }: AttendeeCardProps) {
  function setMetPerson(patch: Partial<NonNullable<InspectionDraft['metPerson']>>): void {
    onDraftChange?.({ metPerson: { ...draft.metPerson, ...patch } })
  }

  const capacityMissing = isBlank(draft.metPerson?.capacity)

  return (
    <>
      <div className={s.identity}>
        <Field label="Prénom de la personne sur place">
          {({ id }) => (
            <Input
              id={id}
              placeholder="À renseigner"
              defaultValue={draft.metPerson?.firstName ?? ''}
              disabled={readOnly}
              onValueChange={(firstName) => setMetPerson({ firstName })}
            />
          )}
        </Field>
        <Field label="Nom de la personne sur place">
          {({ id }) => (
            <Input
              id={id}
              placeholder="À renseigner"
              defaultValue={draft.metPerson?.lastName ?? ''}
              disabled={readOnly}
              onValueChange={(lastName) => setMetPerson({ lastName })}
            />
          )}
        </Field>
        {/* Le téléphone est du prototype, et il manquait : c'est le numéro que l'agent
              rappellera si la personne s'absente en cours de contrôle. */}
        <Field
          label="Téléphone de la personne sur place"
          warning={phoneWarning(draft.metPerson?.phone) ?? undefined}
        >
          {({ id, hintId }) => (
            <Input
              id={id}
              aria-describedby={hintId}
              // `inputMode` et non `type="tel"` : la liste des types d'`Input` est **fermée** à
              // dessein (règle 3 du design system), et c'est `inputMode` qui fait monter le pavé
              // numérique sur la tablette — le besoin réel ici.
              inputMode="tel"
              placeholder="—"
              defaultValue={draft.metPerson?.phone ?? ''}
              disabled={readOnly}
              onValueChange={(phone) => setMetPerson({ phone })}
            />
          )}
        </Field>
      </div>

      {/*
          « En qualité de » est **réclamée avec insistance, jamais exigée** (M20) : elle ne se
          reconstitue pas après coup, mais un `NOT NULL` pousserait à cocher « propriétaire » à
          l'aveugle — un faux vaut moins qu'un trou signalé. Le prototype la marque d'une
          astérisque rouge ; l'astérisque promettrait un blocage qui n'existe pas, la mention
          « à renseigner » dit la même urgence sans mentir.
        */}
      <Field
        label="En qualité de"
        className={capacityMissing ? `${s.capacity} ${claim.claimed}` : s.capacity}
      >
        {({ id }) => (
          <Input
            id={id}
            // `className` cible le conteneur d'`Input` — c'est lui qui porte la bordure, un
            // sélecteur sur l'`input` interne (`border: 0`) ne peindrait jamais rien.
            className={capacityMissing ? claim.claimedInput : undefined}
            placeholder="Choisir ou écrire"
            suggestions={CAPACITY_SUGGESTIONS}
            defaultValue={draft.metPerson?.capacity ?? ''}
            disabled={readOnly}
            onValueChange={(capacity) => setMetPerson({ capacity })}
          />
        )}
      </Field>

      {/* Le liseré ambre de la qualité manquante n'explique pas de lui-même pourquoi il
            n'empêche rien : sans cette glose, l'agent le lit comme un champ à forcer. */}
      <InfoNote className={s.note} icon={<Info size={15} />}>
        La personne rencontrée est celle qui signera. Sa qualité ne se retrouve plus après le
        passage : l'écran la réclame, sans jamais bloquer la transmission.
      </InfoNote>
    </>
  )
}
